export enum EventType {
  CLUB = 'club',
  TOURNAMENT = 'tournament',
  LEAGUE = 'league',
  LADDER_LEAGUE = 'ladder_league',
  TEAM_LEAGUE = 'team_league',
  PICKUP_GAME = 'pickup_game',
}

/** The `module_for` column, shared by matches, schedules and play opportunities. */
export enum SystemModuleFor {
  TOURNAMENT = 4,
  LEAGUE = 12,
  TEAM_LEAGUE = 120,
  PICKUP_GAME = 192,
  PICKUP_GAME_DAY = 209,
  LADDER_LEAGUE = 210,
  MANUALLY_RECORDED_GAME = 214,
  CLUB_EVENT_SESSION = 215,
  CLUB_PRIVATE_PLAY_SESSION = 216,
  CLUB_LESSON_SESSION = 217,
}

export interface EventTimezoneFields {
  /** IANA id; may be an empty string. */
  timezone?: string;
  /** Computed at the entry start date, follows DST. */
  timezoneAbbreviation?: string;
  /** Seconds from UTC, null when unknown. */
  timezoneUtcOffset?: number | null;
}

export interface PlayOpportunityItem extends EventTimezoneFields {
  uuid: string;
  moduleFor: string;
  moduleUuid: string;
  title: string;
  startDate: string;
  endDate: string;
  distanceMiles: number;
  status: string;
  isFavorited: boolean;
  totalCount: number;
  slug: string;
  logo: string;
  city: string;
  state: string;
  stateId: number;
  country: string;
  countryId: number;
  registrationCount: number;
  registrationDateClosed: string;
  /** Computed in the action from city/state/country */
  location: string;
  /** Computed in the action from startDate/endDate */
  dateRange: string;
  /** Computed in the action from location and dateRange */
  locationAndDateRange: string;
}

export interface PlayOpportunitiesApiResponse {
  totalRecords: number;
  currentPage: number;
  pageSize: number;
  nextPage: string;
  prevPage: string;
  results: {
    opportunities: PlayOpportunityItem[];
  };
}

export interface PlayerUpcomingScheduleEvent extends EventTimezoneFields {
  uuid: string;
  moduleFor: SystemModuleFor;
  moduleUuid: string;
  /** Tournament name. getPlayerUpcomingPlaySchedule resolves titleName into this, falling
   * back to the concatenated "<tournament> - <event>" the BE sends when it is absent.
   * Render this — not titleName. */
  title: string;
  /** Raw tournament name from the payload. Only a source for `title`. */
  titleName?: string;
  /** Event name on its own, rendered as the second line. */
  eventName?: string;
  startDate: string;
  endDate: string;
  status: string;
  isFavorited: boolean;
  totalCount: number;
  slug: string;
  logo: string;
  city: string;
  state: string;
  stateId: number;
  country: string;
  countryId: number;
  /** Where the session is played. Often empty outside Game Maker, and may be absent entirely. */
  venue?: string;
  isProgressiveDraw: boolean;
  /** Not returned by /dashboard/schedule yet — pending the same field play-opportunities already has. */
  distanceMiles?: number;
  /** Computed in the action from city/state/country */
  location: string;
  /** Computed in the action from startDate, e.g. `Tue Sep 1 2026`. Used by the day-group headers. */
  startDateLabel: string;
  /** Computed in the action from startDate, e.g. `12:00 PM EDT`. Shown under `startDateLabel` on the cards. */
  startTimeLabel: string;
}

export interface PlayerUpcomingSchedule {
  totalRecords: number;
  currentPage: number;
  pageSize: number;
  nextPage: string;
  prevPage: string;
  results: {
    schedules: PlayerUpcomingScheduleEvent[];
  };
}
